import { Link, useParams } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Card, Spinner } from '@diwata/ironvale'
import { CapabilitySplit } from '@/components/CapabilitySplit'
import { orbitOf, tierGroupOf, TIER_HUE } from './familiarRoster'

// The familiar detail landing: identity + the Covenant at a glance. The capability tags render as
// the Inner/Outer split (conclave_cds_spec §4.1) — the Covenant's security posture is the headline,
// not a settings dump.

const TIER_LINE: Record<string, string> = {
  sovereign: 'Your own familiar and principal, the centre of your constellation.',
  adept: 'A purpose-built specialist, reached through your Sovereign.',
  guild: 'An organisational familiar, owned by a Circle.',
}

export function FamiliarOverview() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()

  const [familiarQ, covenantQ] = useQueries({
    queries: [
      { queryKey: ['familiar', id], queryFn: () => api.getFamiliar(id!), enabled: !!id },
      { queryKey: ['covenant', id], queryFn: () => api.getCovenant(id!), enabled: !!id },
    ],
  })

  const familiar = familiarQ.data
  const covenant = covenantQ.data

  if (familiarQ.isLoading) return <Spinner style={{ display: 'block', margin: '48px auto' }} />
  if (!familiar) return <p className="cp-empty">Familiar not found.</p>

  const group = tierGroupOf(familiar.tier)
  const orbit = orbitOf(group) === 'inner' ? 'Inner' : 'Outer'

  return (
    <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div className="cp-overview-identity">
          {familiar.logo_url || familiar.sigil_url ? (
            <img
              className="cp-overview-sigil"
              src={familiar.logo_url ?? familiar.sigil_url ?? undefined}
              alt=""
            />
          ) : (
            <div className="cp-overview-sigil cp-overview-sigil--mark" style={{ color: TIER_HUE[group] }}>
              {familiar.name.slice(0, 1)}
            </div>
          )}
          <div>
            <div className="cp-familiar-name">{familiar.name}</div>
            <div className="cp-page-subtitle">{TIER_LINE[familiar.tier] ?? familiar.tier}</div>
            <div className="cp-page-subtitle" style={{ marginTop: 4 }}>
              <span style={{ color: TIER_HUE[group] }}>{orbit}</span>
              {covenant ? <> · Covenant v{covenant.version}</> : null}
            </div>
          </div>
        </div>
      </Card>

      {covenantQ.isLoading ? (
        <Spinner style={{ display: 'block', margin: '24px auto' }} />
      ) : covenant ? (
        <>
          <CapabilitySplit tools={covenant.tools} />

          <Card>
            <div className="cp-cap-head">
              <div className="cp-label">Covenant</div>
              <Link to={`/familiar/${id}/covenant`} className="iv-button iv-button--ghost iv-button--sm">
                Open
              </Link>
            </div>
            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
              {covenant.role ? (
                covenant.role.length > 280 ? `${covenant.role.slice(0, 280)}…` : covenant.role
              ) : (
                <span className="cp-empty">No role set</span>
              )}
            </div>
            {covenant.boundaries.length > 0 && (
              <p className="cp-page-subtitle" style={{ marginTop: 8 }}>
                {covenant.boundaries.length} boundar{covenant.boundaries.length === 1 ? 'y' : 'ies'}
              </p>
            )}
          </Card>
        </>
      ) : (
        <p className="cp-empty">No Covenant found.</p>
      )}
    </div>
  )
}
